import React, { FC, useState } from "react";
import { Link } from "react-router-dom";
import { Share, ThreeDots } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { Dropdown } from "react-bootstrap";
import ImagePage from "../img/shared-page.png";
import useGetUrlLocale from "../../../hooks/useGetUrlLocale";
import { UserIcon } from "../../shadedPages/UserIcon/UserIcon";
import PageActions from "../../projects/projectPage/pageActions";
import {
  SharedPagesQuery,
  useSharedPagesQuery,
} from "../../../graphql/access.hooks";
import Pagination from "../../../components/pagination";
import NoShared from "../NoShared";

type PageItemProps = {
  item: SharedPagesQuery["sharedPages"]["pages"][number];
  isLastProject: boolean;
};

type Props = {
  searchTerm: string;
};

export const PagesTable = ({ searchTerm }: Props) => {
  const { t } = useTranslation();

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(12);

  const { data: sharedPagesData, refetch } = useSharedPagesQuery({
    variables: { page: currentPage, limit: limit, search: searchTerm },
  });

  const sharedPages = sharedPagesData?.sharedPages?.pages;

  const pagination = sharedPagesData?.sharedPages?.paginator;
  const pageCount = pagination?.totalPages || 0;
  const totalItems = pagination?.totalItemCount || 0;
  const firstItemIndex = pagination
    ? (currentPage - 1) * pagination.itemNumberPerPage + 1
    : 0;
  const lastItemIndex = pagination
    ? Math.min(currentPage * pagination.itemNumberPerPage, totalItems)
    : 0;

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleLimitChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = Number(event.target.value);
    setLimit(newLimit);
    setCurrentPage(1);
    refetch({
      page: 1,
      limit: newLimit,
      search: searchTerm,
    });
  };

  return (
    <>
      {sharedPages?.length ? (
        <>
          <div className="col-12 table-scroll">
            <table className="table table-index mb-0 overflow-hidden rounded">
              <thead>
                <tr className="bg-disabled table-heading">
                  <th className="text-dark-gray py-3 ps-4 col-1">
                    {t("projects.tablePreview")}
                  </th>
                  <th className="text-dark-gray py-3 col-3">
                    {t("projects.tableTitle")}
                  </th>
                  <th className="text-dark-gray py-3 col-1 text-nowrap text-capitalize">
                    {t("share.tableShareDate")}
                  </th>
                  <th className="text-dark-gray py-3 col-2">
                    {t("projects.tableUsers")}
                  </th>
                  <th className="text-dark-gray py-3 col-1">
                    {t("projects.tableActions")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white table-content border-white">
                {sharedPages?.map(
                  (
                    page: SharedPagesQuery["sharedPages"]["pages"][number],
                    index: number
                  ) => (
                    <tr className="align-middle" key={page.id}>
                      <PageItem
                        item={page}
                        isLastProject={sharedPages.length === index + 1}
                      />
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            t={t}
            pages={pageCount}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            firstItemIndex={firstItemIndex}
            lastItemIndex={lastItemIndex}
            totalItems={totalItems}
            limit={limit}
            onLimitChange={handleLimitChange}
          />
        </>
      ) : (
        <NoShared />
      )}
    </>
  );
};

export const PageItem: FC<PageItemProps> = ({ item, isLastProject }) => {
  const lng = useGetUrlLocale();

  return (
    <>
      <td className="py-3 ps-4">
        <Link
          to={`${process.env.REACT_APP_CONSTRUCTOR_URL}${lng}/${item.id}`}
          className={`page-prew-table d-flex justify-content-center overflow-hidden border rounded`}
        >
          <img
            className="h-100"
            src={item?.image || ImagePage}
            alt={item.title}
          />
        </Link>
      </td>
      <td>
        <p className="table-title text-nowrap m-0">
          {item.title.length > 20
            ? `${item.title.slice(0, 20)}...`
            : item.title}
        </p>
      </td>
      <td className="text-nowrap">
        {item.userPageAccess.createdAt.slice(0, 10)}
      </td>
      <td>
        <div className="d-flex flex-row-reverse justify-content-end">
          {item?.userAccesses?.map(({ email }: { email: string }) => (
            <UserIcon key={email} email={email} />
          ))}
          <UserIcon email={item?.user?.email} className="user-icon-owner" />
        </div>
      </td>
      <td>
        <div className="d-flex align-items-center actions-bar">
          <div>
            <Link
              to={`${lng}/user/access/page/${item.id}/access`}
              className="btn btn-outline-primary primary-hover rounded-1 settings-icon text-blue"
            >
              <Share className="fs-6" />
            </Link>
          </div>
          <Dropdown
            drop={isLastProject ? "up" : "down"}
            className="pe-3 mx-3 dropup"
          >
            <Dropdown.Toggle
              variant="outline-primary"
              id="dropdown-basic"
              className="primary-hover"
            >
              <ThreeDots className="fs-6" />
            </Dropdown.Toggle>
            <PageActions
              type={"page"}
              pageId={item.id}
              accessType={item?.userPageAccess?.accessType}
            />
          </Dropdown>
        </div>
      </td>
    </>
  );
};
